/**
 * Governança - Control Tower (Dashboard Operacional)
 * Redesign minimalista v2 — VUKZ logo, grupos separados
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity, Target, Bot, AlertCircle,
  TrendingUp, Clock, ChevronRight, ExternalLink,
  Server, Database, CheckCircle, XCircle, RefreshCw
} from 'lucide-react';
import { getGovernanceOverview, getGovernanceIncidents, getOpsWorkerStatus, getOpsIngestionDashboard } from '../../services/governance';
import { COLORS } from '../../components/governance/governanceTheme';
import { FEATURES } from '../../config/features';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';
import { getGovernanceT } from '../../i18n/governance';

// ─── Design Primitives ───────────────────────────────────────────────────────
const SectionLabel = ({ children }) => (
  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-4">{children}</p>
);

const Divider = () => <div className="border-t border-gray-100 my-6" />;

const SevBadge = ({ severity }) => {
  const map = {
    critical: 'bg-rose-100 text-rose-700',
    error: 'bg-rose-100 text-rose-700',
    high: 'bg-amber-100 text-amber-700',
    warning: 'bg-amber-100 text-amber-700',
    medium: 'bg-yellow-100 text-yellow-700',
    low: 'bg-blue-100 text-blue-700',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${map[severity] || 'bg-gray-100 text-gray-600'}`}>
      {severity}
    </span>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const GovernanceControlTowerPage = () => {
  const [overview, setOverview] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [workerStatus, setWorkerStatus] = useState(null);
  const [ingestion, setIngestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { lang } = useGovernanceLang();
  const T = getGovernanceT(lang).governance.controlTower;

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [overviewData, incidentsData, workerData, ingestionData] = await Promise.all([
        getGovernanceOverview('production'),
        getGovernanceIncidents({ limit: 10 }, 'production'),
        getOpsWorkerStatus().catch(() => null),
        getOpsIngestionDashboard().catch(() => null)
      ]);
      setOverview(overviewData);
      setIncidents(incidentsData.incidents || []);
      setWorkerStatus(workerData);
      setIngestion(ingestionData);
    } catch (err) {
      console.error('[ControlTower] Error fetching data:', err);
      setError(err.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-64">
        <div className="text-center">
          <RefreshCw className="w-7 h-7 animate-spin text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-400">{T.loading}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-rose-50 border border-rose-100 rounded-xl p-5 flex items-start gap-3">
          <AlertCircle className="w-4 h-4 text-rose-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-rose-700">{T.errorTitle}</p>
            <p className="text-xs text-rose-500 mt-0.5">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const costUsd   = overview?.cost_today?.total_usd ?? 0;
  const costLimit = overview?.cost_today?.daily_limit_usd ?? 100;
  const costPct   = overview?.cost_today?.utilization_pct ?? 0;
  const errorRate = overview?.error_rate?.error_rate_pct ?? 0;
  const p95       = overview?.latency?.p95_latency_ms ?? 0;
  const avgMs     = overview?.latency?.avg_latency_ms ?? 0;

  const workerAlive  = workerStatus?.worker_alive;
  const queueDepth   = workerStatus?.queue_depth ?? '\u2014';
  const completed24h = workerStatus?.last_24h?.completed ?? '\u2014';
  const failed24h    = workerStatus?.last_24h?.failed ?? 0;
  const lastActivity = workerStatus?.last_activity?.processed_at
    ? new Date(workerStatus.last_activity.processed_at).toLocaleString(T.dateLocale, {
        hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit',
      })
    : 'N/A';

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── Masthead ────────────────────────────────────────────────────── */}
      <div className="bg-gray-950 px-8 py-5 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img
            src="/vukz-logo.png"
            alt="Vukz"
            className="w-10 h-10 rounded-xl object-contain"
            onError={e => { e.target.style.display = 'none'; }}
          />
          <div>
            <h1 className="text-white text-xl font-black tracking-tight">Control Tower</h1>
            <p className="text-gray-400 text-xs mt-0.5">{T.subtitle}</p>
          </div>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-gray-300 border border-gray-700 rounded-lg hover:bg-gray-800 transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>

      {/* ── Content ─────────────────────────────────────────────────────── */}
      <div className="px-8 py-8 max-w-6xl">

        {/* ── Grupo: Operações Hoje ───────────────────────────────────── */}
        <SectionLabel>{T.sectionNow || 'Operações Hoje'}</SectionLabel>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">

          {/* Cost Today */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center">
                <TrendingUp className="w-3.5 h-3.5 text-amber-500" />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Cost Today</span>
            </div>
            <div className="text-3xl font-black text-gray-900">${costUsd.toFixed(2)}</div>
            <div className="flex items-center gap-2 mt-2.5">
              <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${costPct >= 90 ? 'bg-rose-400' : costPct >= 75 ? 'bg-amber-400' : 'bg-green-400'}`}
                  style={{ width: `${Math.min(100, costPct)}%` }}
                />
              </div>
              <span className={`text-[10px] font-bold ${costPct >= 90 ? 'text-rose-600' : costPct >= 75 ? 'text-amber-600' : 'text-gray-400'}`}>
                {costPct.toFixed(0)}%
              </span>
            </div>
            <p className="text-[10px] text-gray-400 mt-1.5">Limite ${costLimit.toFixed(2)}</p>
          </div>

          {/* Error Rate */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-lg bg-rose-50 flex items-center justify-center">
                <AlertCircle className="w-3.5 h-3.5 text-rose-400" />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Error Rate 24h</span>
            </div>
            <div className={`text-3xl font-black ${errorRate > 5 ? 'text-rose-600' : errorRate > 2 ? 'text-amber-600' : 'text-gray-900'}`}>
              {errorRate.toFixed(2)}%
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
              {overview?.error_rate?.error_count ?? 0} erros · {overview?.error_rate?.total_calls ?? 0} calls
            </p>
          </div>

          {/* P95 Latency */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                <Clock className="w-3.5 h-3.5 text-blue-400" />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">P95 Latency</span>
            </div>
            <div className={`text-3xl font-black ${p95 > 2000 ? 'text-rose-600' : p95 > 1000 ? 'text-amber-600' : 'text-gray-900'}`}>
              {p95}ms
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
              Avg {avgMs}ms ·{' '}
              <span className={p95 > 2000 ? 'text-rose-500 font-bold' : p95 > 1000 ? 'text-amber-500 font-bold' : ''}>
                {p95 > 2000 ? 'SLOW' : p95 > 1000 ? 'DEGRADED' : 'OK'}
              </span>
            </p>
          </div>
        </div>

        <Divider />

        {/* ── Grupo: Worker & Pipeline ─────────────────────────────────── */}
        <SectionLabel>{T.sectionWorker || 'Worker & Pipeline'}</SectionLabel>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">

          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center gap-2 mb-4">
              <Server className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Worker</span>
            </div>
            <div className="flex items-center gap-2">
              {workerAlive
                ? <><CheckCircle className="w-5 h-5 text-green-500" /><span className="text-base font-black text-green-700">ALIVE</span></>
                : <><XCircle className="w-5 h-5 text-rose-500" /><span className="text-base font-black text-rose-700">DOWN</span></>
              }
            </div>
            <p className="text-[10px] text-gray-400 mt-2">{lastActivity}</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center gap-2 mb-4">
              <Database className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Fila</span>
            </div>
            <div className="text-3xl font-black text-gray-900">{queueDepth}</div>
            <p className="text-[10px] text-gray-400 mt-2">Mensagens pendentes</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="w-3.5 h-3.5 text-green-400" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Completos 24h</span>
            </div>
            <div className="text-3xl font-black text-green-700">{completed24h}</div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center gap-2 mb-4">
              <XCircle className="w-3.5 h-3.5 text-rose-400" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Falhos 24h</span>
            </div>
            <div className={`text-3xl font-black ${failed24h > 0 ? 'text-rose-600' : 'text-gray-900'}`}>{failed24h}</div>
          </div>
        </div>

        {/* Ingestion rates sub-group */}
        {ingestion && (
          <div className="grid grid-cols-3 gap-4 mb-6">
            {[
              { label: 'Pass Rate (7d)',  value: ingestion.validation_rates?.pass_rate_pct?.toFixed(1)  ?? '\u2014', color: 'text-green-700' },
              { label: 'Warn Rate (7d)',  value: ingestion.validation_rates?.warn_rate_pct?.toFixed(1)  ?? '\u2014', color: 'text-amber-600' },
              { label: 'Block Rate (7d)', value: ingestion.validation_rates?.block_rate_pct?.toFixed(1) ?? '\u2014', color: 'text-rose-600'  },
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-white rounded-xl border border-gray-100 p-4">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">{label}</p>
                <span className={`text-2xl font-black ${color}`}>{value}<span className="text-sm ml-0.5">%</span></span>
              </div>
            ))}
          </div>
        )}

        <Divider />

        {/* ── Grupo: Atalhos ───────────────────────────────────────────── */}
        <SectionLabel>{T.sectionActions || 'Atalhos'}</SectionLabel>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
          {[
            {
              to: '/governance/planner',
              icon: <Target className="w-4 h-4 text-gray-600" />,
              iconBg: 'bg-gray-100',
              title: 'Strategic Planner',
              sub: 'WSJF prioritization',
              desc: T.plannerDesc,
              hoverBorder: 'hover:border-gray-300',
            },
            // Gate P1: tile do Builder oculto quando GOV_BUILDER_ENABLED=false
            ...(FEATURES.GOV_BUILDER_ENABLED ? [{
              to: '/governance/builder',
              icon: <Activity className="w-4 h-4 text-purple-600" />,
              iconBg: 'bg-purple-50',
              title: 'Builder',
              sub: 'Chat + Tasks + PM',
              desc: T.builderDesc,
              hoverBorder: 'hover:border-purple-200',
            }] : []),
            {
              to: '/governance/agents',
              icon: <Bot className="w-4 h-4 text-blue-600" />,
              iconBg: 'bg-blue-50',
              title: 'AI Agents',
              sub: T.agentsSubtitle,
              desc: T.agentsDesc,
              hoverBorder: 'hover:border-blue-200',
            },
          ].map(({ to, icon, iconBg, title, sub, hoverBorder }) => (
            <Link
              key={to}
              to={to}
              className={`bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3 ${hoverBorder} hover:shadow-sm transition-all group`}
            >
              <div className={`w-9 h-9 rounded-lg ${iconBg} flex items-center justify-center flex-shrink-0`}>
                {icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-gray-900">{title}</p>
                <p className="text-[10px] text-gray-400">{sub}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 flex-shrink-0" />
            </Link>
          ))}
        </div>

        <Divider />

        {/* ── Grupo: Incidentes Recentes ───────────────────────────────── */}
        <div className="flex items-center justify-between mb-4">
          <SectionLabel>{T.sectionIncidents || 'Incidentes Recentes'}</SectionLabel>
          <Link
            to="/governance/incidents"
            className="text-[10px] font-bold uppercase tracking-widest text-gray-400 hover:text-gray-600 flex items-center gap-1 -mt-4"
          >
            {T.viewAll || 'Ver todos'} <ExternalLink className="w-3 h-3" />
          </Link>
        </div>

        {incidents.length === 0 ? (
          <div className="bg-green-50 border border-green-100 rounded-xl p-5 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-green-700">{T.noIncidents}</p>
              <p className="text-xs text-green-500 mt-0.5">{T.noIncidentsDesc}</p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-50">
                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-gray-400">Sev</th>
                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-gray-400">Agente</th>
                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-gray-400">Mensagem</th>
                  <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-gray-400">Quando</th>
                </tr>
              </thead>
              <tbody>
                {incidents.map((incident, idx) => (
                  <tr key={incident.id || idx} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                    <td className="px-5 py-3"><SevBadge severity={incident.severity} /></td>
                    <td className="px-5 py-3 font-mono text-xs font-semibold text-gray-700">{incident.agent_name}</td>
                    <td className="px-5 py-3 text-xs text-gray-500 max-w-xs truncate">
                      {incident.error_message || incident.message || '\u2014'}
                    </td>
                    <td className="px-5 py-3 text-[10px] text-gray-400 whitespace-nowrap">
                      {incident.timestamp
                        ? new Date(incident.timestamp).toLocaleString(T.dateLocale, {
                            day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                          })
                        : '\u2014'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </div>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <div className="px-8 pb-8">
        <p className="text-[10px] text-gray-400">
          {T.footer} · {new Date().toLocaleString(T.dateLocale)}
        </p>
      </div>

    </div>
  );
};

export default GovernanceControlTowerPage;

